import { getTorchGlowCanvas } from './GradientCache';

export interface TextOptions {
  color?: string;
  fontSize?: number;
  fontFamily?: string;
  align?: CanvasTextAlign;
  baseline?: CanvasTextBaseline;
}

export interface RectOptions {
  fillColor?: string;
  strokeColor?: string;
  strokeWidth?: number;
}

export class Renderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  // Font string cache to avoid repeated string concatenation
  private _lastFontSize: number = 0;
  private _lastFontFamily: string = '';
  private _lastFontString: string = '';

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context');
    }
    this.ctx = ctx;

    // Default settings
    this.ctx.imageSmoothingEnabled = false; // Pixel art style
  }

  get width(): number {
    return this.canvas.width;
  }

  get height(): number {
    return this.canvas.height;
  }

  get context(): CanvasRenderingContext2D {
    return this.ctx;
  }

  clear(color: string = '#2d241c'): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  drawRect(
    x: number,
    y: number,
    width: number,
    height: number,
    options: RectOptions = {}
  ): void {
    const { fillColor, strokeColor, strokeWidth = 1 } = options;

    if (fillColor) {
      this.ctx.fillStyle = fillColor;
      this.ctx.fillRect(x, y, width, height);
    }

    if (strokeColor) {
      this.ctx.strokeStyle = strokeColor;
      this.ctx.lineWidth = strokeWidth;
      this.ctx.strokeRect(x, y, width, height);
    }
  }

  drawCircle(
    x: number,
    y: number,
    radius: number,
    options: RectOptions = {}
  ): void {
    const { fillColor, strokeColor, strokeWidth = 1 } = options;

    this.ctx.beginPath();
    this.ctx.arc(x, y, radius, 0, Math.PI * 2);

    if (fillColor) {
      this.ctx.fillStyle = fillColor;
      this.ctx.fill();
    }

    if (strokeColor) {
      this.ctx.strokeStyle = strokeColor;
      this.ctx.lineWidth = strokeWidth;
      this.ctx.stroke();
    }
  }

  drawText(text: string, x: number, y: number, options: TextOptions = {}): void {
    const {
      color = '#f4e8d4',
      fontSize = 16,
      fontFamily = 'Arial, sans-serif',
      align = 'left',
      baseline = 'top',
    } = options;

    this.ctx.fillStyle = color;
    // Cache font string to avoid repeated concatenation
    if (fontSize !== this._lastFontSize || fontFamily !== this._lastFontFamily) {
      this._lastFontSize = fontSize;
      this._lastFontFamily = fontFamily;
      this._lastFontString = `${fontSize}px ${fontFamily}`;
    }
    this.ctx.font = this._lastFontString;
    this.ctx.textAlign = align;
    this.ctx.textBaseline = baseline;
    this.ctx.fillText(text, x, y);
  }

  drawImage(
    image: HTMLImageElement | HTMLCanvasElement,
    x: number,
    y: number,
    width?: number,
    height?: number
  ): void {
    if (width !== undefined && height !== undefined) {
      this.ctx.drawImage(image, x, y, width, height);
    } else {
      this.ctx.drawImage(image, x, y);
    }
  }

  drawSprite(
    image: HTMLImageElement,
    sx: number,
    sy: number,
    sw: number,
    sh: number,
    dx: number,
    dy: number,
    dw: number,
    dh: number
  ): void {
    this.ctx.drawImage(image, sx, sy, sw, sh, dx, dy, dw, dh);
  }

  // Isometric helpers
  drawIsometricTile(
    x: number,
    y: number,
    tileWidth: number,
    tileHeight: number,
    color: string
  ): void {
    const halfW = tileWidth / 2;
    const halfH = tileHeight / 2;

    this.ctx.beginPath();
    this.ctx.moveTo(x, y - halfH); // Top
    this.ctx.lineTo(x + halfW, y); // Right
    this.ctx.lineTo(x, y + halfH); // Bottom
    this.ctx.lineTo(x - halfW, y); // Left
    this.ctx.closePath();

    this.ctx.fillStyle = color;
    this.ctx.fill();
    this.ctx.strokeStyle = 'rgba(0,0,0,0.3)';
    this.ctx.lineWidth = 1;
    this.ctx.stroke();
  }

  // Convert cartesian to isometric coordinates
  cartToIso(cartX: number, cartY: number): { x: number; y: number } {
    return {
      x: (cartX - cartY),
      y: (cartX + cartY) / 2,
    };
  }

  // Convert isometric to cartesian coordinates
  isoToCart(isoX: number, isoY: number): { x: number; y: number } {
    return {
      x: (2 * isoY + isoX) / 2,
      y: (2 * isoY - isoX) / 2,
    };
  }

  // Save and restore context state
  save(): void {
    this.ctx.save();
  }

  restore(): void {
    this.ctx.restore();
  }

  // Set global alpha
  setAlpha(alpha: number): void {
    this.ctx.globalAlpha = alpha;
  }

  // Reset alpha
  resetAlpha(): void {
    this.ctx.globalAlpha = 1;
  }

  renderTorchLighting(
    torchPositions: Array<{ x: number; y: number }>,
    camera: { worldToScreen: (x: number, y: number) => { x: number; y: number }; zoom: number }
  ): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    const glowCanvas = getTorchGlowCanvas(camera.zoom);
    const halfW = glowCanvas.width / 2;
    const halfH = glowCanvas.height / 2;
    const yOffset = 20 * camera.zoom;

    for (const torch of torchPositions) {
      const screenPos = camera.worldToScreen(torch.x, torch.y);

      // Skip if off screen
      if (screenPos.x < -200 || screenPos.x > this.canvas.width + 200 ||
          screenPos.y < -200 || screenPos.y > this.canvas.height + 200) {
        continue;
      }

      ctx.drawImage(glowCanvas, screenPos.x - halfW, screenPos.y - yOffset - halfH);
    }

    ctx.restore();
  }
}
